import { enableProdMode, ErrorHandler, importProvidersFrom, inject, Injectable, Injector, isDevMode, provideAppInitializer } from '@angular/core';


import { environment } from '@cashbox-env/environment';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideToastr, ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@jsverse/transloco';
import { HttpErrorResponse, HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  BASE_PATH,
  ComponentType,
  flattenObject,
  HX_APP_VERSION,
  HX_COMPONENT_NAME,
  HX_ENV_MODE,
  HxError,
  initKeycloak,
  provideKeycloakFn,
  provideTranslocoFn,
  UiError,
} from 'hx-services';
import { APP_VERSION } from '@cashbox-env/version';
import { bootstrapApplication } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppComponent } from './app/app.component';
import * as Sentry from '@sentry/browser';
import { provideRouter, withRouterConfig } from '@angular/router';
import { createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, includeBearerTokenInterceptor } from 'keycloak-angular';
import { ROUTES } from '@cashbox-app/app.routes';
import Keycloak from 'keycloak-js';
import { catchError } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs';
import { addI18n } from 'hx-component';
import { addOrderI18n } from 'hx-order';

// const ex: HxError = error.error?.message ? error.error as HxError : {
//         code: 577,
//         id: '-',
//         message: error.message,
//         data: error.error ?? {},
//       };
// const toastr = this.injector.get(ToastrService);
// const tr = this.injector.get(TranslocoService);

if (environment.sentryUrl) {
  Sentry.init({
    release: environment.version,
    environment: environment.production ? 'production' : 'staging',
    ignoreErrors: ['Unauthorized', 'productBalance.zero'],
    dsn: environment.sentryUrl,
    sampleRate: 0,
    tracePropagationTargets: [],
    beforeSend: (event, hint) => {
      console.log('[sentry] before send', event, hint);
      if (hint && hint.originalException && hint.originalException instanceof HttpErrorResponse) {
        if (hint.originalException.status === 401) {
          console.log('[sentry] skipped error logging', event);
          return null;
        }
      }
      return event;
    },
    integrations: [
      // new Sentry.Integrations.TryCatch({
      //   XMLHttpRequest: false,
      // }),
    ],
  });
}

const customHttpInterceptorFn: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(Keycloak);
  const toastr = inject(ToastrService);
  const tr = inject(TranslocoService);

  let headers = req.headers.set('Accept-Language', tr.getActiveLang());
  if (isDevMode()) {
    headers = headers.set('component', ComponentType.cb);
  }
  return next(req.clone({headers: headers})).pipe(catchError(error => {
    if (error instanceof HttpErrorResponse) {
      if (error.error && error.error.code) {
        if (error.error.code === 120) {
          toastr.warning('sale.after.operday');
        }
      }

      if (error.status === 401) {
        if (!req.url.includes('127.0.0.1')) {
          keycloak.logout();
        }
      } else if (error.status === 502 || error.status === 500 || error.status === 504) {
        toastr.error(tr.translate('http-interceptor.error', {error: error.status}));
        toastr.info(tr.translate('http-interceptor.loaderPage'));
      }
    }

    return observableThrowError(error);
  }));
};

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {
  }

  handleError(error: any) {
    if (error.promise && error.rejection) {
      console.error('[got-rejection] %O', error);
      error = error.rejection;
    }
    const toastr = this.injector.get(ToastrService);
    const tr = this.injector.get(TranslocoService);
    if (error instanceof HttpErrorResponse) {
      const ex: HxError = error.error?.message ? error.error as HxError : {
        code: 577,
        id: '-',
        message: error.message,
        data: error.error ?? {},
      };
      toastr.error(ex.description ?? tr.translate(ex.message, flattenObject(ex.data)), tr.translate('error') + ' ' + ex.id);
    }
    if (error instanceof UiError) {
      toastr.error(tr.translate(error.message, error.data ?? {}));
    } else {
      console.error('[error-handler] %O', error);
    }
    Sentry.captureException(error.originalError || error);
  }
}

function getKeycloakUrl() {
  if (environment.production) {
    return location.origin.replace(location.origin.split('//')[1].split('.')[0], 'auth') + '/auth';
  }
  return environment.keycloakUrl;
}

if (environment.production) {
  enableProdMode();
}

if (environment.production) {
  console.log = () => {};
}

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^\/api\/.*/,
  bearerPrefix: 'Bearer',
});

bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(FormsModule, ReactiveFormsModule),
      provideRouter(ROUTES, withRouterConfig({
        // scrollPositionRestoration: 'disabled'
      })),
      provideTranslocoFn(),
      provideKeycloakFn({
        url: getKeycloakUrl(),
        realm: environment.keycloakRealm,
        clientId: environment.keycloakClientId,
      }),
      provideAppInitializer(() => {
        const tr = inject(TranslocoService);
        addI18n(tr);
        addOrderI18n(tr);
        return initKeycloak();
      }),
      provideHttpClient(withInterceptors([includeBearerTokenInterceptor, customHttpInterceptorFn])),
      provideEnvironmentNgxMask(),
      provideAnimations(),
      provideToastr(),
      {
        provide: BASE_PATH,
        useValue: ''
      },
      {
        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
        useValue: [urlCondition]
      },
      {
          provide: ErrorHandler,
          useClass: CustomErrorHandler
      },
      {
          provide: HX_COMPONENT_NAME,
          useValue: ComponentType.cb
      },
      {
          provide: HX_APP_VERSION,
          useValue: APP_VERSION
      },
      { provide: HX_ENV_MODE, useValue: environment.production },
    ]
})
  .catch(err => console.error(err));
